/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

// tslint:disable:no-magic-numbers

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { TranslationService } from '@gh/core-mls';
import { interval$ } from '@gh/rx';
import { first$ } from '@gh/rx/operators';
import { find, isNil } from 'lodash';
import { Observable, Subscription } from 'rxjs';

export interface TimerInterval {
  id: string;
  millis: number;
  label: string;
}

const TIME_NAME_TO_MILLIS = {
  'ms': 1,
  's': 1000,
  'm': 1000 * 60,
  'h': 1000 * 60 * 60,
};

const TIME_RE = /^\s*(\d+)\s*(ms|s|m|h)\s*$/;

function parseTime(time: string): number {
  const match = TIME_RE.exec(time);

  if (isNil(match)) {
    throw new Error('Timer component: time interval cannot be parsed');
  }

  return Number(match[1]) * TIME_NAME_TO_MILLIS[match[2]];
}

export interface TimerEvent {
  waitFor(ob$: Observable<any>): void;
}

@Component({
  selector: 'gh-timer',
  template: require('./timer.component.html'),
  styles: [require('./timer.component.scss')],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimerComponent implements DoCheck, OnDestroy {
  @Input() label: string;

  @Output() intervalChange = new EventEmitter<string>();
  @Output() timer = new EventEmitter<TimerEvent>();

  currentInterval?: TimerInterval;
  predefinedIntervals: TimerInterval[];

  private _switchedOn = true;
  private _waiting = false;
  private _running = true;
  private _disabled = false;
  private _resetTimer = true;
  private intervalToSelect?: string;
  private timerSubscription?: Subscription;
  private waitingSubscription?: Subscription;

  constructor(private translationService: TranslationService,
              private changeDetectorRef: ChangeDetectorRef) {

  }

  @Input()
  @HostBinding('class.timer-disabled')
  set disabled(disabled: boolean) {
    this._disabled = disabled;
    this.resetTimer();
  }

  get disabled(): boolean {
    return this._disabled;
  }

  get intervalDisabled(): boolean {
    return !this._switchedOn;
  }

  @Input()
  set running(running: boolean) {
    this._running = running;
    this.resetTimer();
  }

  get running(): boolean {
    return this._running;
  }

  @Input()
  set switchedOn(switchedOn: boolean) {
    this._switchedOn = switchedOn;
    this.resetTimer();
  }

  get switchedOn(): boolean {
    return this._switchedOn;
  }

  @Input()
  set interval(interval: string) {
    this.intervalToSelect = interval;
    this.changeDetectorRef.markForCheck();
  }

  @Input()
  set intervals(intervals: string[]) {
    this.predefinedIntervals = intervals.map((interval) => ({
      id: interval,
      millis: parseTime(interval),
      label: this.translationService.get(`lbl_timer:${interval}`),
    }));
  }

  ngDoCheck(): void {
    if (this.intervalToSelect) {
      this.selectInterval(this.intervalToSelect);
      this.intervalToSelect = void 0;
    }

    if (!this._resetTimer) {
      return;
    }

    this.stopTimer();
    this.startTimer();

    this._resetTimer = false;
  }

  ngOnDestroy(): void {
    this.stopTimer();
    this.stopWaiting();
  }

  changeInterval(interval: TimerInterval): void {
    this.currentInterval = interval;
    this.resetTimer();

    this.intervalChange.emit(interval.id);
  }

  waitFor(ob$: Observable<any>): void {
    this.stopWaiting();

    this.setWaiting(true);
    const stopWaiting = this.stopWaiting.bind(this);
    this.waitingSubscription = ob$.pipe(first$()).subscribe(stopWaiting, stopWaiting, stopWaiting);
  }

  setWaiting(waiting: boolean): void {
    this._waiting = waiting;
    this.resetTimer();
  }

  private selectInterval(interval: string): void {
    const newInterval = find(this.predefinedIntervals, { id: interval });

    if (newInterval && this.currentInterval !== newInterval) {
      this.currentInterval = newInterval;
      this.resetTimer();
    } else {
      this.currentInterval = void 0;
    }
  }

  private resetTimer(): void {
    this._resetTimer = true;
    this.changeDetectorRef.markForCheck();
  }

  private stopTimer(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
  }

  private stopWaiting(): void {
    if (this.waitingSubscription) {
      this.waitingSubscription.unsubscribe();
    }
    this.setWaiting(false);
  }

  private startTimer(): void {
    if (!this._running || !this._switchedOn || this._disabled || this._waiting || isNil(this.currentInterval)) {
      return;
    }

    this.timerSubscription = interval$(this.currentInterval.millis).pipe(first$()).subscribe(() => {
      this.timer.emit(this);
      this.resetTimer();
    });
  }
}
